import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Select, MenuItem, FormControl } from "@material-ui/core";
import { Button } from "../../Button/Button";
import { getKeyboardKey } from "../../wcag/getKeyboardKey";
import { FieldLabel } from "../FieldLabel/FieldLabel";
import { useIsMobile } from "../../utils/useIsMobile";
import { mergeRefs, useFocusVisibleRef } from "common-react";
import { Badge } from "../../Badge/Badge";
import { DropdownIconIndicator } from "../Dropdown/components/DropdownIconIndicator";
import { Checkbox } from "../../Checkbox/Checkbox";
import { Typography } from "../../Typography/Typography";

import useStyles from "./DropdownMulti.style";

const PaperWithFooter = React.forwardRef<HTMLDivElement, any>(({ children, footer, className, ...rest }, ref) => {
  return (
    <div ref={ref} className={className} {...rest}>
      {children}
      {footer}
    </div>
  );
});
PaperWithFooter.displayName = 'PaperWithFooter';

const sanitizeSelectedValues = (selected: unknown, allowedOptions: string[]): string[] => {
  if (!Array.isArray(allowedOptions)) {
    return [];
  }
  const allowed = new Set(allowedOptions);
  if (!Array.isArray(selected)) {
    return [];
  }
  return selected.filter(
    (item): item is string =>
      typeof item === "string" && item.length > 0 && allowed.has(item)
  );
};

export interface CustomDropdownMultiProps {
  options: string[];
  onChange: (value: string[]) => void;
  onActionButtonClick?: (value: string[]) => void;
  value: string[];
  label: string;
  valueToLabel?: (value: string) => string;
  labelId: string;
  className?: string;
  placeholder?: string;
  actionButtonLabel?: string;
  multiple?: boolean;
  enableListenerOfExternalValueChange?: boolean;
  required?: boolean;
  disabled?: boolean;
  testId?: string;
  autoTestId?: string;
  optionListAutoTestId?: string;
  fullWidth?: boolean;
}

export const DropdownMulti: React.FC<CustomDropdownMultiProps> = ({
  options,
  onChange,
  value: externalValue,
  labelId,
  label,
  className,
  placeholder,
  valueToLabel = (valueToMap: string): string => valueToMap,
  onActionButtonClick,
  actionButtonLabel,
  multiple = true,
  enableListenerOfExternalValueChange = false,
  disabled = false,
  required = false,
  testId = 'default-test-id',
  autoTestId,
  optionListAutoTestId,
  fullWidth = false,
}) => {
  const c = useStyles({ value: externalValue, placeholder, multiple });
  const [t] = useTranslation("translation");
  const isMobile = useIsMobile("xs");
  const selectRef = useRef();
  const firstMenuItemRef = useRef<HTMLLIElement>(null);
  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(() =>
    sanitizeSelectedValues(externalValue, options)
  );
  const { ref: inputRef, isFocusVisible } = useFocusVisibleRef();

  useEffect(() => {
    if (enableListenerOfExternalValueChange) {
      setInternalValue(sanitizeSelectedValues(externalValue, options));
    }
  }, [externalValue, enableListenerOfExternalValueChange, options]);

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => {
      if (firstMenuItemRef.current) {
        firstMenuItemRef.current.focus();
      }
    }, 0);
  };

  const handleClose = () => {
    setIsOpen(false);
    setInternalValue(sanitizeSelectedValues(externalValue, options));
  };

  const handleInternalChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setInternalValue(sanitizeSelectedValues(event.target.value, options));
  };

  const handleActionButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(false);
    const sanitizedSelection = sanitizeSelectedValues(internalValue, options);
    setInternalValue(sanitizedSelection);
    onChange(sanitizedSelection);
    onActionButtonClick?.(sanitizedSelection);
  };

  const handleMenuItemKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, option: string) => {
    const { isTabKey, isSpaceKey } = getKeyboardKey(e.nativeEvent);

    if (isTabKey || isSpaceKey) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isTabKey) {
      actionButtonRef.current?.focus();
    }

    if (isSpaceKey) {
      setInternalValue(previousSelection =>
        previousSelection.includes(option)
          ? previousSelection.filter(selectedOption => selectedOption !== option)
          : [...previousSelection, option]
      );
    }
  };

  const handleActionButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const { isTabKey } = getKeyboardKey(e.nativeEvent);
    if (isTabKey) {
      e.preventDefault();
      e.stopPropagation();
      firstMenuItemRef.current?.focus();
    }
  };

  return (
    <FormControl className={c.formControl} fullWidth={fullWidth}>
      <FieldLabel id={labelId}>{label}</FieldLabel>
      <Select
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple={multiple}
        disabled={disabled}
        fullWidth={fullWidth}
        IconComponent={DropdownIconIndicator}
        required={required}
        renderValue={() => (
          <div className={c.renderValue}>
            {placeholder}
            <Badge className={clsx(c.numberOfOptions, !internalValue.length  && c.numberOfOptionsEmpty)} badgeContent={internalValue.length} />
          </div>
        )}
        ref={mergeRefs(selectRef, inputRef)}
        disableUnderline
        displayEmpty
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: isMobile ? "center" : "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: isMobile ? "center" : "left",
          },
          getContentAnchorEl: null,
          anchorEl: selectRef.current,
          PopoverClasses: {
            paper: c.popover,
          },
          PaperProps: ({
            component: PaperWithFooter,
            footer: (
              <div className={c.actionButtonBox}>
                <Button
                  onClick={handleActionButtonClick as () => void}
                  ref={actionButtonRef}
                  onKeyDown={handleActionButtonKeyDown}
                  fullWidth={true}
                  buttonVariant='medium'
                >
                  {actionButtonLabel || t("filter")}
                </Button>
              </div>
            ),
          } as React.ComponentProps<typeof PaperWithFooter>),
          MenuListProps: {
            ...(optionListAutoTestId && { 'data-autotestid': optionListAutoTestId })
          } as Record<string, string | undefined>,
          disableAutoFocusItem: true,
        }}
        className={clsx(c.root, className, isFocusVisible ? "Mui-focusVisible" : "",)}
        classes={{
          icon: c.icon,
        }}
        data-autotestid={autoTestId}
        data-testid={testId}
        labelId={labelId}
        value={internalValue}
        onChange={handleInternalChange}
      >
        {options.map((option, index) => (
          <MenuItem
            className={c.menuItemMulti}
            key={option}
            value={option}
            ref={index === 0 ? firstMenuItemRef : null}
            tabIndex={index === 0 ? 0 : -1}
            onKeyDown={e => handleMenuItemKeyDown(e as React.KeyboardEvent<HTMLLIElement>, option)}
          >
            <span className={c.menuItemContent}>
              <Checkbox checked={internalValue.indexOf(option) > -1} />
              <Typography className={c.menuItemTextMulti}>{valueToLabel(option)}</Typography>
            </span>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
